export function applyFormAction(event: MouseEvent | Event) {
  // Resolve the actual button (inline onclick or delegated listeners may change event.currentTarget)
  const evtTarget = (event as Event).target as HTMLElement | null;
  const evtCurrent = (event as Event).currentTarget as HTMLElement | null;
  const source = evtTarget ?? evtCurrent;
  const button = (source && (source.tagName === 'BUTTON' ? source as HTMLButtonElement : source.closest('button'))) as HTMLButtonElement | null;

  if (!button) {
    return;
  }

  const formActionAttr = button.getAttribute('formaction') ?? '';
  if (!formActionAttr) {
    return;
  }

  // If the formaction is already absolute or root-relative, use it as-is
  if (/^(https?:)?\/\//.test(formActionAttr) || formActionAttr.startsWith('/')) {
    button.formAction = formActionAttr;
    const form = button.form ?? button.closest('form');
    if (form) {
      (form as HTMLFormElement).action = formActionAttr;
    }
    return;
  }

  // Build a path-based relative URL using the current pathname (preserves PathBase in production)
  const url = new URL(window.location.href);
  url.searchParams.delete('handler');
  url.searchParams.set('handler', formActionAttr);

  const params = url.searchParams.toString();
  const relative = url.pathname + (params ? `?${params}` : '') + (url.hash ?? '');

  // Set both the button's formAction property and the form.action to be robust across browsers/environments
  button.formAction = relative;
  const form = button.form ?? button.closest('form');
  if (form) {
    (form as HTMLFormElement).action = relative;
  }

  console.log('applyFormAction ->', relative);
}


export function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay = 300,
  immediate = false
): ((...args: Parameters<T>) => void) & { cancel: () => void } {
  let timeout: ReturnType<typeof setTimeout> | undefined;

  type Debounced = ((...args: Parameters<T>) => void) & { cancel: () => void };

  const debounced = function (this: any, ...args: Parameters<T>) {
    const later = () => {
      timeout = undefined;
      if (!immediate) func.apply(this, args);
    };

    const callNow = immediate && timeout === undefined;

    if (timeout !== undefined) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(later, delay);

    if (callNow) {
      func.apply(this, args);
    }
  } as Debounced;

  debounced.cancel = () => {
    if (timeout !== undefined) {
      clearTimeout(timeout);
      timeout = undefined;
    }
  };

  return debounced;
}
